/**
 * MobileDeviceSheet — P0-5 (Etapa 4 da Sprint Fleet Explorer Finalização).
 *
 * Bottom-sheet de dispositivo para MOBILE. SOMENTE LEITURA — espelha o
 * resumo do painel direito (desktop) num formato condensado. Reusa os
 * mesmos dados (DTO `device` + getDeviceConfigSnapshot) e as classes
 * .ipwd-rp-* de estilo. NENHUMA ação de escrita: só "Abrir Automação",
 * "Abrir Detalhes" e fechar.
 *
 * Mantido separado do DeviceDetailPanel de propósito: o painel desktop
 * (Etapa 3, aprovado) não é tocado; aqui o layout é um resumo.
 */
import { useEffect, useState } from "react";
import { getDeviceConfigSnapshot } from "../../lib/api.js";
import { resolveActiveSchedule } from "../../lib/scheduleCore.js";
import DeviceCommands from "./DeviceCommands.jsx";
import DeviceDiagnostics from "./DeviceDiagnostics.jsx";
import ScheduleV2Card from "./ScheduleV2Card.jsx";
import { getProtectionState } from "../../lib/protection.js";

const _DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function val(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  return v;
}

// MAP-HEALTH-1B — "último boot" aproximado (now − uptime_s). "~há" = estimativa.
function uptimeAprox(s) {
  if (s == null) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  const d = Math.floor(n / 86400);
  const h = Math.floor((n % 86400) / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (d > 0) return `~há ${d}d ${h}h`;
  if (h > 0) return `~há ${h}h ${m}m`;
  return `~há ${m}m`;
}

function scoreColor(score) {
  if (score == null) return "#7e8aa0";
  if (score >= 80) return "#35E8A4";
  if (score >= 50) return "#FFB224";
  return "#f87171";
}

function Row({ label, value, mono = false, highlight = null }) {
  const has = value !== null && value !== undefined && value !== "";
  return (
    <div className="ipwd-rp-row">
      <span className="ipwd-rp-row-label">{label}</span>
      {has ? (
        <span
          className={"ipwd-rp-row-val" + (mono ? " is-mono" : "")}
          style={highlight ? { color: highlight } : undefined}
        >
          {value}
        </span>
      ) : (
        <span className="ipwd-rp-na">não disponível</span>
      )}
    </div>
  );
}

export default function MobileDeviceSheet({ device, onClose, onOpenDevice, onCommand }) {
  const [snap, setSnap] = useState(null);
  const [snapState, setSnapState] = useState("idle"); // idle|loading|ok|error

  const deviceId = device?.device_id || null;

  useEffect(() => {
    if (!deviceId) return;
    let alive = true;
    const ctrl = new AbortController();
    setSnap(null);
    setSnapState("loading");
    getDeviceConfigSnapshot(deviceId, { signal: ctrl.signal })
      .then((data) => {
        if (!alive) return;
        setSnap(data?.last_config_snapshot || null);
        setSnapState("ok");
      })
      .catch(() => {
        if (!alive) return;
        setSnapState("error");
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [deviceId]);

  if (!device) return null;

  const online = !!device.online;
  const relayOff = device.relay === "off";
  const score = device.health_score;

  // MAP-UX-1C — derivados de status (render-puro do DTO) p/ o mobile compacto.
  const fwStale = !!(
    device.fw_version && device.target_release_version &&
    device.fw_version !== device.target_release_version
  );
  const protectedDev = getProtectionState(device).protected;
  const hmacInsecure = device.secure_channel === false && device.auth_secret_set === true;
  const cloneSuspect = device.clone_suspected === true;

  // INA226-2 — Energia compacta. GATE: só p/ modelo compatível (device comum
  // não mostra nada). Mostra Potência (resumo) + status do sensor.
  const powerSupported = device.power_monitor_supported === true;
  const ina226Status = val(device.ina226_status);
  const showPower = powerSupported || device.bus_voltage_mv != null ||
    device.current_ma != null || device.power_mw != null || device.ina226_ok != null;
  const powerBad = device.ina226_ok === false || ina226Status === "missing" || ina226Status === "error";
  const fmtA = (ma) => (ma == null ? null : (Math.abs(ma) < 1000 ? `${ma} mA` : `${(ma / 1000).toFixed(2)} A`));

  const clienteLabel =
    val(device.customer_name) ||
    val(device.customer_code) ||
    null;

  // Watchdog resumido.
  const wd = snap?.watchdog || null;
  const wdMode = val(wd?.mode) ?? val(device.watchdog_mode);
  const wdLoading = snapState === "loading";
  const wdResumo = wd
    ? [
        wd.enabled === true ? "ativo" : wd.enabled === false ? "inativo" : null,
        val(wdMode),
        val(wd.action),
      ].filter(Boolean).join(" · ") || null
    : null;

  // S2.9 — agenda ATIVA resolvida do snapshot (v1 OU v2). Fonte = device.
  const activeSched = resolveActiveSchedule(snap, device);
  const schedEntries = activeSched.active.entries;
  const e0 = schedEntries[0] || null;
  const agendaResumo = e0
    ? [
        e0.time || "--:--",
        (e0.days || []).map((d) => _DOW[d]).join(", ") || null,
        e0.action || "relay_reboot",
        schedEntries.length > 1 ? `+${schedEntries.length - 1}` : null,
        `exec: ${activeSched.activeSource}`,
      ].filter(Boolean).join(" · ")
    : null;

  return (
    <>
      <div className="ipwd-mobile-sheet-backdrop" onClick={onClose} />
      <div className="ipwd-mobile-sheet" role="dialog" aria-label="Detalhe do dispositivo">
        <div className="ipwd-sheet-handle" />

        {/* Cabeçalho */}
        <div className="ipwd-rp-head">
          <span className="ipwd-rp-title">{device.device_id}</span>
          <button
            type="button"
            className="ipwd-rp-close"
            title="Fechar"
            onClick={onClose}
          >×</button>
        </div>

        <div className="ipwd-rp-status">
          <span className={online ? "is-online" : "is-offline"}>
            {online ? "● ONLINE" : "● OFFLINE"}
          </span>
          {score != null && (
            <span className="ipwd-rp-score" style={{ color: scoreColor(score) }}>
              Score {score}
            </span>
          )}
        </div>

        {/* Resumo */}
        <div className="ipwd-rp-section">
          <Row label="Cliente" value={clienteLabel} />
          <Row
            label="Relé"
            value={val(device.relay) ? device.relay.toUpperCase() : null}
            highlight={relayOff ? "#FF8A3C" : online ? "#35E8A4" : null}
          />
          <Row
            label="RSSI"
            value={device.rssi != null ? `${device.rssi} dBm` : null}
            highlight={device.weak_signal ? "#FFB224" : null}
          />
          <Row
            label="Firmware"
            value={val(device.fw_version)}
            mono
            highlight={fwStale ? "#FFB224" : null}
          />
          {/* MAP-UX-1C — proteção/segurança compacta: protegido (roxo,
              inequívoco) + alertas só quando há problema. */}
          <Row
            label="Proteção"
            value={protectedDev ? "produção · denylist" : "—"}
            highlight={protectedDev ? "#A78BFA" : null}
          />
          {hmacInsecure && (
            <Row label="HMAC" value="inseguro" highlight="#f87171" />
          )}
          {cloneSuspect && (
            <Row label="Clone" value="suspeito" highlight="#f87171" />
          )}
          {/* INA226-2 — Energia compacta: só p/ modelo compatível. */}
          {showPower && (
            <>
              <Row
                label="Energia"
                value={device.power_mw != null ? `${(device.power_mw / 1000).toFixed(2)} W` : null}
                highlight={device.power_mw != null ? "#2BD9F0" : null}
              />
              {device.bus_voltage_mv != null && (
                <Row label="Tensão" value={`${(device.bus_voltage_mv / 1000).toFixed(2)} V`} />
              )}
              {device.current_ma != null && (
                <Row label="Corrente" value={fmtA(device.current_ma)} />
              )}
              {(powerBad || device.ina226_ok !== true) && (
                <Row
                  label="Sensor"
                  value={ina226Status || (device.ina226_ok === false ? "indisponível" : "—")}
                  highlight={powerBad ? "#FFB224" : null}
                />
              )}
            </>
          )}
          <Row
            label="Watchdog"
            value={wdLoading ? "carregando…" : wdResumo}
          />
          <Row
            label="Agenda"
            value={wdLoading ? "carregando…" : agendaResumo}
          />
          {/* MAP-HEALTH-1B — saúde recente compacta (informativa; 0 = neutro,
              âmbar só quando > 0). Boot total = histórico. Último boot aprox. */}
          <Row
            label="Reboots 24h"
            value={val(device.reboot_count_24h)}
            highlight={device.reboot_count_24h > 0 ? "#FFB224" : null}
          />
          <Row
            label="Watchdog 24h"
            value={val(device.watchdog_count_24h)}
            highlight={device.watchdog_count_24h > 0 ? "#FFB224" : null}
          />
          <Row label="Boot total" value={val(device.boot_count)} mono />
          <Row label="Último boot" value={uptimeAprox(device.uptime_s)} />
        </div>

        {/* S2.6 — Agenda V2 persistida (DORMENTE). Só aparece se houver entries. */}
        <ScheduleV2Card snapshot={snap} />

        {/* Diagnóstico — principais motivos (read-only, compacto) */}
        <DeviceDiagnostics deviceId={device.device_id} max={3} />

        {/* Comandos do device — mesmos handlers seguros do MapView */}
        <DeviceCommands device={device} onCommand={onCommand} />

        {/* Navegação (sem ações destrutivas) */}
        <div className="ipwd-rp-actions">
          <button
            type="button"
            className="ipwd-rp-navbtn"
            onClick={() => onOpenDevice?.(device.device_id, "automacao")}
          >
            Abrir Automação
          </button>
          <button
            type="button"
            className="ipwd-rp-navbtn is-primary"
            onClick={() => onOpenDevice?.(device.device_id, "cockpit")}
          >
            Abrir Detalhes
          </button>
        </div>
      </div>
    </>
  );
}
